import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import Select from '@/components/forms/Select'
import { toast } from '@/components/toasts/toast.helpers'
import {
  createNewVoipCampaign,
  fetchAllOrganizationVoipCampaigns,
  deleteOrganizationsVoipCampaign,
  updateVoipCampaign,
  fetchOrganizationsAndConfigs,
  massUpdateVoipCampaigns,
  massDeleteVoipCampaigns,
} from '@/reducers/organizations/organizationVoipCampaigns.actions'
import {
  setCurrentFilter,
  setLoading,
} from '@/reducers/organizations/organizationVoipCampaigns.redux'
import { getHierarchyOrganizationOptions, getOrganizationOptions } from '@/utils/helpers'
import { BulkVoipCampaignUploadButton } from '@/views/Organizations/components/buttons/BulkVoipCampaignUploadButton'
import { setFilters } from '@/reducers/callSearch/callSearch.redux'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { openModal, closeModal } from '@/reducers/ui/ui.redux'
import { Button } from '@/components/buttons/Button'
import { PageHeader } from '@/components/headers/PageHeader'
import { FormField } from '@/components/forms/FormField'
import { FilterForm } from '@/components/filters/FilterForm'

import { AddCampaignModal } from './components/AddCampaignModal'
import { CampaignTable } from './components/CampaignTable'
import {
  formatMappedCampaigns,
  formatUnmappedCampaigns,
  getTableData,
  createDropdownOptions,
} from './helpers'
import { VOIP_CAMPAIGN_FILTERS, FILTER_OPTIONS } from './constants'

export const PlaybookMappingPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const configs = useSelector((state) => state.playbooks.playbooks)
  const { mappedCampaigns, unmappedCampaigns, currentFilter, loading } = useSelector(
    (state) => state.organizationVoipCampaigns
  )
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const { organizations } = useSelector((state) => state)
  const ogranizationOptions = getOrganizationOptions(organizations)
  const hierarchyOrganizationOptions = getHierarchyOrganizationOptions(flattenedUserOrgHierarchy)
  const {
    organizationid: currentUserOrg,
    hierarchy_manager,
    edit_config,
  } = useSelector((state) => state.currentUser)

  const isBaltoAdmin = currentUserOrg === 1
  const showManagerHierarchyOrgDropdown = !isBaltoAdmin && hierarchy_manager

  useEffect(() => {
    if (!edit_config) {
      history.push(`/`)
    }
  })

  const availableOrgOptions = showManagerHierarchyOrgDropdown
    ? hierarchyOrganizationOptions
    : ogranizationOptions

  const [loadedOrgData, setLoadedOrgData] = useState(null)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('')
  const activeFilter = currentFilter

  const loadVoipCampaignsForOrg = (orgId = selectedOrganizationId) => {
    if (!orgId) return

    dispatch(
      fetchAllOrganizationVoipCampaigns({
        organizationId: orgId,
      })
    )
  }

  useEffect(() => {
    if (!selectedOrganizationId) {
      if (isBaltoAdmin) {
        const org1 = availableOrgOptions.find((org) => String(org.value) === '1')
        if (org1) {
          setSelectedOrganizationId(org1.value)
          return
        }
      }

      setSelectedOrganizationId(currentUserOrg)
    }
  }, [availableOrgOptions, selectedOrganizationId, isBaltoAdmin])

  const handleFilterChange = (newFilter) => {
    dispatch(setCurrentFilter(newFilter))
  }

  useEffect(() => {
    if (!selectedOrganizationId) return

    const loadOrgData = async () => {
      dispatch(setLoading({ table: true }))

      try {
        await dispatch(fetchOrganizationsAndConfigs({ organizationId: selectedOrganizationId }))
        await dispatch(
          fetchAllOrganizationVoipCampaigns({ organizationId: selectedOrganizationId })
        )
      } finally {
        dispatch(setLoading({ table: false }))
      }
    }

    loadOrgData()
  }, [selectedOrganizationId])

  useEffect(() => {
    if (selectedOrganizationId && loadedOrgData === selectedOrganizationId) {
      dispatch(setLoading({ table: true }))
      loadVoipCampaignsForOrg()
    }
  }, [loadedOrgData])

  const handleUpdateFilters = (section, value) => {
    dispatch(setFilters({ [section]: value }))
  }

  const handleOrgSelect = (option) => {
    const newOrgId = option.value
    setSelectedOrganizationId(newOrgId)
    handleUpdateFilters('organizationId', newOrgId)
    setLoadedOrgData(null)
  }

  const handleCreateCampaign = async (payload) => {
    await dispatch(createNewVoipCampaign(payload))
    dispatch(setCurrentFilter(VOIP_CAMPAIGN_FILTERS.ALL))
  }

  const handleUpdateCampaign = async (payload, editingCampaign) => {
    await dispatch(
      updateVoipCampaign({
        ...payload,
        voipCampaignUuid: editingCampaign.uuid,
      })
    )
  }

  const handleConvertUnmappedCampaign = async (payload) => {
    await dispatch(createNewVoipCampaign(payload))
    dispatch(setCurrentFilter(VOIP_CAMPAIGN_FILTERS.ALL))
  }

  const handleMassUpdate = async ({ campaignUuids = [], campaignNames = [], cid }) => {
    await dispatch(
      massUpdateVoipCampaigns({
        organizationId: selectedOrganizationId,
        campaignUuids,
        campaignNames,
        cid,
        reloadOrganizationsVoipCampaigns: () => loadVoipCampaignsForOrg(),
      })
    )
  }

  const handleMassDelete = async ({ campaignUuids }) => {
    await dispatch(
      massDeleteVoipCampaigns({
        organizationId: selectedOrganizationId,
        campaignUuids,
        reloadOrganizationsVoipCampaigns: () => loadVoipCampaignsForOrg(),
      })
    )
  }

  const handleModalSubmit = async ({
    campaignName,
    configCid,
    isEdit,
    isUnmatchedEdit,
    editingCampaign,
  }) => {
    if (!campaignName || !configCid) return

    dispatch(setLoading({ modal: true }))

    const payload = {
      cid: configCid,
      campaignName,
      organizationId: selectedOrganizationId,
      reloadOrganizationsVoipCampaigns: () => loadVoipCampaignsForOrg(),
    }

    try {
      if (isEdit && editingCampaign) {
        if (isUnmatchedEdit) {
          await handleConvertUnmappedCampaign(payload)
        } else {
          await handleUpdateCampaign(payload, editingCampaign)
        }
      } else {
        await handleCreateCampaign(payload)
      }

      dispatch(closeModal())
    } catch (error) {
      toast.error('Couldn’t save the campaign. Please try again.', { cause: error })
    } finally {
      dispatch(setLoading({ modal: false }))
    }
  }

  const handleAddCampaign = () => {
    dispatch(openModal('voip-campaign-add'))
  }

  const handleDeleteOrganizationsVoipCampaign = ({ campaignUuids }) => {
    if (Array.isArray(campaignUuids)) {
      if (campaignUuids.length > 1) {
        dispatch(
          massDeleteVoipCampaigns({
            organizationId: selectedOrganizationId,
            campaignUuids,
            reloadOrganizationsVoipCampaigns: () => loadVoipCampaignsForOrg(),
          })
        )
      } else {
        dispatch(
          deleteOrganizationsVoipCampaign({
            voipCampaignUuid: campaignUuids[0],
            organizationId: selectedOrganizationId,
            reloadOrganizationsVoipCampaigns: () => loadVoipCampaignsForOrg(),
          })
        )
      }
    } else {
      dispatch(
        deleteOrganizationsVoipCampaign({
          voipCampaignUuid: campaignUuids,
          organizationId: selectedOrganizationId,
          reloadOrganizationsVoipCampaigns: () => loadVoipCampaignsForOrg(),
        })
      )
    }
  }

  const dropdownOptions = createDropdownOptions(configs, selectedOrganizationId)
  const formattedMappedCampaigns = formatMappedCampaigns(mappedCampaigns, configs)
  const formattedUnmappedCampaigns = formatUnmappedCampaigns(unmappedCampaigns)
  const tableRows = getTableData(activeFilter, formattedMappedCampaigns, formattedUnmappedCampaigns)

  return (
    <>
      <PageHeader title="Playbook Mapping" />
      {selectedOrganizationId && (
        <div>
          <FilterForm
            small
            sticky={false}
            inlineActions="end"
            actions={
              <div className="flex-align-center medium-gap">
                <FormField data-testid="filter-dropdown">
                  <Select
                    isClearable={false}
                    placeholder="Select filter"
                    options={FILTER_OPTIONS}
                    value={activeFilter}
                    onChange={(option) => {
                      handleFilterChange(option.value)
                    }}
                  />
                </FormField>

                <BulkVoipCampaignUploadButton organizationId={selectedOrganizationId} />

                <Button brand onClick={handleAddCampaign} data-testid="add-campaign-button">
                  Add Campaign
                </Button>
              </div>
            }
          >
            {isBaltoAdmin && (
              <OrganizationFilter
                options={availableOrgOptions}
                value={selectedOrganizationId}
                onChange={handleOrgSelect}
                isClearable={false}
                data-testid="organization-dropdown"
              />
            )}
          </FilterForm>

          <CampaignTable
            rows={tableRows}
            loading={loading.table}
            onEditSubmit={handleModalSubmit}
            onDelete={handleDeleteOrganizationsVoipCampaign}
            onBulkUpdate={handleMassUpdate}
            onBulkDelete={handleMassDelete}
            dropdownOptions={dropdownOptions}
            modalLoading={loading.modal}
          />
        </div>
      )}

      <AddCampaignModal
        onSubmit={handleModalSubmit}
        dropdownOptions={dropdownOptions}
        loading={loading.modal}
      />
    </>
  )
}
